import React, { useState } from 'react';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { observer } from 'mobx-react-lite';
import { Campaign } from '@vromo/models';
import styled from '@emotion/styled';
import theme from '@vromo/ui/components/theme';
import { darken } from 'polished';
import CampaignEditor from './CampaignEditor';
import Tooltip from '@vromo/ui/components/Tooltip';
import { qaLabelCampaigns } from './../../../../common-qa-labels/campaigns';
import { buildDynamicLabel } from './../../../../common-qa-labels/utils';

const Card = styled.div`
    background-color: white;
    box-shadow: 0 2px 5px 1px rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    margin: 8px;
    overflow: hidden;
    width: 450px;
    user-select: none;

    @media (max-width: 950px) {
        width: auto;
    }
`;

const Header = styled.div`
    background-color: ${({ background }: any) => background};
    height: 60px;
    padding: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const Action = styled.a`
    font-size: 18px;
    margin-left: 8px;
    color: ${({ color }: any) => color};
    cursor: pointer;

    &:hover {
        color: ${({ color }: any) => darken(0.05, color)};
    }
`;

interface Props {
    campaign: Campaign;
    deleteCampaign: () => void;
}

export default observer(function CampaignCard({ campaign, deleteCampaign }: Props) {
    const [editing, setEditing] = useState(false);

    return (
        <>
            <Card
                data-campaign-name={campaign.name}
                data-testid={buildDynamicLabel(qaLabelCampaigns.campaignCardTitle, campaign.name)}>
                <Header background={campaign.theme.background}>
                    {campaign.logo && campaign.logo.url ? (
                        <img src={campaign.logo.url} style={{ height: 35 }} />
                    ) : (
                        <div />
                    )}
                    <div>
                        <Tooltip position="bottom" label="Edit">
                            <Action
                                data-testid={qaLabelCampaigns.editButton}
                                data-edit-button
                                color={theme.text.color(campaign.theme.background)}
                                onClick={() => setEditing(true)}>
                                <FontAwesome icon="pencil" />
                            </Action>
                        </Tooltip>

                        <Tooltip position="bottom" label="Delete">
                            <Action
                                data-testid={qaLabelCampaigns.deleteButton}
                                data-delete-button
                                color={theme.text.color(campaign.theme.background)}
                                onClick={deleteCampaign}>
                                <FontAwesome icon="trash" />
                            </Action>
                        </Tooltip>
                    </div>
                </Header>
                <div
                    style={{
                        height: 15,
                        backgroundColor: campaign.theme.accent,
                        borderBottom: `1px solid ${
                            campaign.theme.accent.toUpperCase() === '#FFFFFF'
                                ? '#d8d8d8'
                                : 'transparent'
                        }`
                    }}
                />
                <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ marginRight: 16, wordBreak: 'break-word', flexShrink: 0 }}>
                        <label style={{ display: 'block' }}>Name</label>
                        {campaign.name}
                    </div>
                    <div style={{ marginRight: 16, flexShrink: 0 }}>
                        <label style={{ display: 'block' }}>Google Analytics</label>
                        {(campaign.analytics && campaign.analytics.google) || <i>None</i>}
                    </div>
                    <div style={{ marginRight: 16 }}>
                        <label style={{ display: 'block' }}>SMS Template</label>
                        {campaign.link.sms || <i>None</i>}
                    </div>
                    <div style={{ marginRight: 16, flexShrink: 0, textAlign: 'center' }}>
                        <label style={{ display: 'block' }}>Map Icon</label>
                        <img
                            src={
                                campaign.theme.icon
                                    ? campaign.theme.icon.url
                                    : require('@vromo/icons/car/icon.png')
                            }
                            style={{
                                transform: 'rotate(45deg)',
                                maxWidth: 40,
                                maxHeight: 40,
                                margin: 4
                            }}
                        />
                    </div>
                </div>
            </Card>
            <CampaignEditor
                isOpen={editing}
                campaign={campaign}
                onCancel={() => setEditing(false)}
                onSave={() => setEditing(false)}
            />
        </>
    );
});
